import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { NAV } from "@/components/nav";
import { Logo, BrandLockup } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Menu, X, Search, LogOut, RotateCcw, Bell, AlertTriangle, FileText, Flame, Calendar, HardHat, ChevronDown, Globe, ShieldCheck, Check, Crown, Fingerprint, ArrowLeft, Boxes } from "lucide-react";
import { toast } from "sonner";
import { ProgereThemeSwitcher } from "@/components/ProgereThemeSwitcher";
import { OnboardingAI } from "@/components/OnboardingAI";
import { ProgereLegalFooter } from "@/components/ProgereLegalFooter";
import { PageGuide } from "@/components/PageGuide";
import { MagicInput } from "@/components/MagicInput";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { SupportWidget } from "@/components/SupportWidget";
import { ProgereModuleIdentity } from "@/progere-design-system/ProgereModuleIdentity";
import { ProgereVersionBadge } from "@/progere-design-system/ProgereVersionBadge";
import { ProgereReleasePopup } from "@/progere-design-system/ProgereReleasePopup";
import { SupportAccessBanner } from "@/components/SupportAccessBanner";
import { ProgereDownloadAppButton } from "@/components/shared/ProgereDownloadAppButton";
import { useLang } from "@/i18n";

const NOTIF_ICON = { alert: AlertTriangle, file: FileText, flame: Flame, calendar: Calendar };
const NOTIF_ROUTE = { alert: "/sante", file: "/soumissions", flame: "/priorites", calendar: "/calendrier" };
const PRIO_TONE = { urgente: "text-[#F10D19]", importante: "text-[#F2BC2E]", normale: "text-gray-400" };
const CORE_URL = process.env.REACT_APP_CORE_URL || "https://progere.ca";
const CORE_MODULES_URL = `${CORE_URL}/modules`;
const ECO_CATALOG = [
  { code: "crm", name: "PROGÈRE CRM" },
  { code: "punch", name: "PROGÈRE PUNCH" },
  { code: "erp", name: "PROGÈRE ERP" },
  { code: "compta", name: "PROGÈRE COMPTA" },
  { code: "paie", name: "PROGÈRE PAIE" },
  { code: "devis", name: "PROGÈRE DEVIS AI" },
];

function SidebarContent({ onNavigate }) {
  const { t } = useLang();
  const [open, setOpen] = useState(() => ({ ...Object.fromEntries(NAV.map((g) => [g.section, true])), __eco__: true }));
  const toggle = (s) => setOpen((o) => ({ ...o, [s]: !o[s] }));
  const [ecoMods, setEcoMods] = useState(["crm"]);
  useEffect(() => {
    api.get("/ecosystem/access").then((r) => setEcoMods(["crm", ...((r.data && r.data.modules) || [])])).catch(() => {});
  }, []);
  return (
    <nav className="flex flex-col gap-2 p-3 pg-scroll overflow-y-auto h-full">
      <a href={CORE_MODULES_URL} data-testid="back-to-core"
        className="mx-1 flex items-center gap-2 px-2.5 py-2 rounded-lg text-[12px] font-semibold text-gray-400 hover:text-[#F2BC2E] hover:bg-white/[0.04] transition-colors">
        <ArrowLeft size={14} /> {t("Retour à PROGÈRE CORE")}
      </a>
      <ProgereModuleIdentity />
      {NAV.map((group) => (
        <div key={group.section}>
          <button onClick={() => toggle(group.section)} data-testid={`section-${group.section}`}
            className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-md hover:bg-white/[0.03] transition-colors">
            <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-gray-500">{t(group.section)}</span>
            <ChevronDown size={13} className={`text-gray-600 transition-transform duration-200 ${open[group.section] ? "" : "-rotate-90"}`} />
          </button>
          {open[group.section] && (
            <div className="flex flex-col gap-1.5 mt-1 mb-1">
              {group.items.map((it, i) => (
                <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.testid} onClick={onNavigate}
                  className={({ isActive }) =>
                    `pg-nav-item flex items-center gap-3 px-2.5 py-2 rounded-lg text-[13px] font-medium tracking-wide transition-all duration-200 ${
                      isActive
                        ? "text-white border-l-[3px] border-[#FFD256] pg-nav-active bg-gradient-to-r from-[#F10D19] via-[#DC0914] to-[#A7060E] shadow-[0_5px_16px_rgba(241,13,25,0.28)]"
                        : "text-gray-300 hover:text-white hover:bg-white/[0.05] border-l-[3px] border-transparent"}`}>
                  {({ isActive }) => (
                    <>
                      <it.icon size={16} strokeWidth={1.9}
                        className={`shrink-0 ${isActive ? "text-white" : (i % 2 === 0 ? "text-[#F2BC2E]" : "text-[#F10D19]")}`} />
                      {t(it.label)}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      ))}
      <div className="mt-2">
        <button onClick={() => toggle("__eco__")} data-testid="section-ecosystem"
          className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-md hover:bg-white/[0.03] transition-colors">
          <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-gray-500">{t("Écosystème PROGÈRE")}</span>
          <ChevronDown size={13} className={`text-gray-600 transition-transform duration-200 ${open["__eco__"] ? "" : "-rotate-90"}`} />
        </button>
        {open["__eco__"] && (
        <>
        <div className="space-y-0.5 mt-1" data-testid="ecosystem-modules">
          {ECO_CATALOG.map((m) => {
            const active = ecoMods.includes(m.code);
            const href = active ? CORE_MODULES_URL : `${CORE_URL}/modules/${m.code}`;
            return (
              <a key={m.code} href={href} data-testid={`eco-${m.code}`}
                className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[12.5px] text-gray-300 hover:text-white hover:bg-white/[0.04] transition-colors">
                <Boxes size={14} strokeWidth={1.8} className={`shrink-0 ${active ? "text-[#F2BC2E]" : "text-gray-600"}`} />
                <span className="flex-1 truncate">{m.name}</span>
                {active
                  ? <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#F2BC2E]/15 text-[#F2BC2E]">{t("Actif")}</span>
                  : <span className="text-[8px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/[0.06] text-gray-500">{t("Découvrir")}</span>}
              </a>
            );
          })}
        </div>
        <a href={CORE_MODULES_URL} data-testid="ecosystem-core-hub"
          className="mt-1 flex items-center gap-2 px-2.5 py-2 rounded-md text-[12px] font-semibold text-[#F2BC2E] hover:bg-[#F2BC2E]/[0.08] transition-colors">
          <Boxes size={13} /> {t("Gérer mes modules dans PROGÈRE CORE")}
        </a>
        </>
        )}
      </div>
      <div className="mt-auto pt-3 px-2 border-t border-white/5 space-y-2">
        <ProgereDownloadAppButton variant="sidebar" size="sm" moduleCode="crm" />
        <ProgereVersionBadge />
      </div>
    </nav>
  );
}

function LangSwitcher() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const langs = [{ c: "fr", n: "Français" }, { c: "en", n: "English" }];
  return (
    <div className="relative">
      <button data-testid="lang-switcher-btn" onClick={() => setOpen(!open)}
        className="text-gray-400 hover:text-[#F2BC2E] transition-colors flex items-center gap-1" title={t("Langue")}>
        <Globe size={18} /><span className="text-[10px] font-bold uppercase">{lang}</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-3 w-40 bg-[#141414] border border-white/10 rounded-xl overflow-hidden z-50 shadow-2xl" data-testid="lang-menu">
            {langs.map((l) => (
              <button key={l.c} data-testid={`lang-${l.c}`} onClick={() => { setLang(l.c); setOpen(false); }}
                className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 flex items-center justify-between">
                {l.n}{lang === l.c && <Check size={14} className="text-[#F2BC2E]" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!search) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get(`/search?q=${encodeURIComponent(search)}`); setResults(r.data); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const moduleRoute = { prospects: "/prospects", clients: "/clients", contacts: "/contacts",
    opportunities: "/opportunites", quotes: "/soumissions", tasks: "/taches", events: "/calendrier",
    communications: "/communications", documents: "/documents", team: "/utilisateurs", integrations: "/integrations",
    templates: "/modeles", tickets: "/support", goals: "/objectifs" };

  const resetDemo = async () => {
    try { await api.post("/demo/reset"); toast.success("Données démo réinitialisées"); setTimeout(() => window.location.reload(), 600); }
    catch { toast.error("Action non autorisée"); }
  };

  const urgentCount = notifs.filter((n) => n.priority === "urgente").length;

  return (
    <div className="progere-app-shell min-h-screen flex">
      <aside className="hidden lg:block w-[240px] shrink-0 border-r border-white/5 h-screen sticky top-0 z-40" style={{ background: "var(--pg-sidebar)" }}>
        <SidebarContent />
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-[240px] border-r border-white/5 h-full" style={{ background: "var(--pg-sidebar)" }}>
            <button className="absolute top-4 right-4 text-gray-400 z-10" onClick={() => setMobileOpen(false)} data-testid="close-mobile-nav"><X size={20} /></button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-50 backdrop-blur-xl border-b border-white/5 px-4 lg:px-8 py-3 flex items-center gap-2 lg:gap-4" style={{ background: "color-mix(in srgb, var(--pg-topbar) 80%, transparent)" }}>
          <button className="lg:hidden text-gray-300 shrink-0" onClick={() => setMobileOpen(true)} data-testid="open-mobile-nav"><Menu size={22} /></button>

          <a href="/aujourdhui" data-testid="header-brand" className="shrink-0 font-display font-black tracking-tight text-base sm:text-lg mr-1 lg:mr-3">
            <span style={{ color: "#bd952a" }}>PRO</span><span style={{ color: "#ac2029", marginLeft: "-0.03em" }}>GÈRE</span> <span style={{ color: "#cfd1d7" }}>CRM</span>
          </a>

          <button className="sm:hidden text-gray-300 shrink-0" onClick={() => setSearchOpen((v) => !v)} data-testid="mobile-search-toggle"><Search size={20} /></button>

          <div className={`relative flex-1 max-w-md ${searchOpen ? "block absolute left-4 right-4 top-full mt-2 z-50" : "hidden"} sm:block sm:static sm:left-auto sm:right-auto sm:mt-0 sm:max-w-md`}>
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input data-testid="global-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t("Recherche globale...")} onBlur={() => setTimeout(() => setResults([]), 200)}
              className="w-full bg-[#0f0f0f] border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm text-white placeholder-gray-600 focus:border-[#F2BC2E] focus:outline-none transition-colors" />
            {results.length > 0 && (
              <div className="absolute mt-2 w-full bg-[#141414] border border-white/10 rounded-xl overflow-hidden z-50 shadow-xl" data-testid="search-results">
                {results.map((r, i) => (
                  <button key={i} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-[#1a1a1a] flex justify-between"
                    onMouseDown={() => { navigate(r.module === "clients" && r.id ? `/client/${r.id}` : (moduleRoute[r.module] || "/")); setSearch(""); setResults([]); setSearchOpen(false); }}>
                    <span>{r.label}</span><span className="text-xs text-gray-600">{r.module}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-3 ml-auto">
            {user?.is_demo && (
              <button onClick={resetDemo} data-testid="reset-demo-btn"
                className="hidden sm:flex items-center gap-2 text-xs font-semibold text-[#F2BC2E] border border-[#F2BC2E]/30 rounded-full px-3 py-1.5 hover:bg-[#F2BC2E]/10 transition-colors">
                <RotateCcw size={13} /> {t("Réinitialiser démo")}
              </button>
            )}
            <LangSwitcher />
            <MagicInput />
            <PageGuide />
            <span className="hidden sm:block"><ProgereThemeSwitcher /></span>
            {user?.is_platform_admin && (
              <button onClick={() => navigate("/centre-maitre")} data-testid="master-center-btn"
                className="text-[#F2BC2E] hover:text-[#F10D19] transition-colors" title={t("Centre Maître")}>
                <Crown size={18} />
              </button>
            )}
            <button onClick={() => navigate("/securite")} data-testid="security-shortcut-btn"
              className="hidden sm:block text-gray-400 hover:text-[#F2BC2E] transition-colors" title={t("Sécurité")}>
              <ShieldCheck size={18} />
            </button>
            <div className="relative">
              <button className="text-gray-400 hover:text-[#F2BC2E] transition-colors relative" data-testid="notifications-btn" onClick={() => setNotifOpen(!notifOpen)}>
              <Bell size={18} />
              {urgentCount > 0 && <span className="absolute -top-1.5 -right-1.5 bg-[#F10D19] text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{urgentCount}</span>}
            </button>
              {notifOpen && (
                <>
                  <div className="fixed inset-0 z-[55]" onClick={() => setNotifOpen(false)} />
                  <div className="fixed left-2 right-2 top-[62px] sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-3 w-auto sm:w-80 bg-[#141414] border border-white/10 rounded-xl overflow-hidden z-[60] shadow-2xl" data-testid="notifications-panel">
                    <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
                      <span className="font-display font-bold uppercase text-sm text-white">{t("Notifications")}</span>
                      <span className="text-xs text-gray-500">{notifs.length}</span>
                    </div>
                    <div className="max-h-96 overflow-y-auto pg-scroll">
                      {notifs.length === 0 && <p className="px-4 py-6 text-sm text-gray-600 text-center">{t("Aucune notification")}</p>}
                      {notifs.map((n, i) => {
                        const Icon = NOTIF_ICON[n.icon] || Bell;
                        return (
                          <button key={i} data-testid={`notif-item-${i}`}
                            onClick={() => { setNotifOpen(false); navigate(NOTIF_ROUTE[n.icon] || "/aujourdhui"); }}
                            className="w-full text-left flex items-start gap-3 px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                            <Icon size={16} className={`mt-0.5 shrink-0 ${PRIO_TONE[n.priority]}`} />
                            <div><p className="text-sm text-gray-300">{n.message}</p>
                              <span className={`text-[10px] uppercase font-bold ${PRIO_TONE[n.priority]}`}>{n.priority}</span></div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F2BC2E] to-[#F10D19] flex items-center justify-center text-black text-xs font-bold">{(user?.name || "U").slice(0, 1)}</div>
              <div className="hidden md:block leading-tight">
                <p className="text-xs font-semibold text-white">{user?.name}</p>
                <p className="text-[10px] text-gray-500 uppercase">{user?.role}</p>
              </div>
            </div>
            <button onClick={logout} data-testid="logout-btn" className="text-gray-400 hover:text-[#F10D19] transition-colors"><LogOut size={18} /></button>
          </div>
        </header>

        {user?.is_demo && (
          <div data-testid="demo-banner" className="w-full bg-[#F2BC2E] text-black text-center font-extrabold uppercase tracking-wider text-[11px] sm:text-xs py-1.5 px-4">
            {t("Mode démonstration — les données sont fictives")}
          </div>
        )}

        <main className="flex-1 p-4 lg:p-8 pb-24 lg:pb-8 pg-scroll">
          <div className="mb-4"><SupportAccessBanner /></div>
          <AnimatePresence mode="wait">
            <motion.div key={location.pathname}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.32, ease: [0.2, 0.7, 0.3, 1] }}>
              {children}
            </motion.div>
          </AnimatePresence>
          <ProgereLegalFooter inApp showVersion />
        </main>
      </div>
      <MobileBottomNav onMenu={() => setMobileOpen(true)} />
      <SupportWidget />
      <OnboardingAI />
      <ProgereReleasePopup />
    </div>
  );
}
